/**
 * ReferralCodeCard - Displays user's referral code with copy and share functionality.
 * Features a prominent code display with animated interactions.
 */

import { useState, useCallback } from "react";
import { trackEvent } from "@jumpbean/analytics";
import type { ReferralCode } from "../../hooks/useReferrals";
import { Button } from "../Button";

export interface ReferralCodeCardProps {
  code: ReferralCode | null;
  isGenerating: boolean;
  onGenerateCode: () => Promise<ReferralCode>;
  onShare: () => void;
}

export function ReferralCodeCard({
  code,
  isGenerating,
  onGenerateCode,
  onShare,
}: ReferralCodeCardProps) {
  const [copied, setCopied] = useState(false);
  const [copyAnimating, setCopyAnimating] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code.code);
      setCopied(true);
      setCopyAnimating(true);

      // Track the copy event
      trackEvent("referral_code_copied", { code: code.code });

      // Reset after animation
      setTimeout(() => setCopied(false), 2000);
      setTimeout(() => setCopyAnimating(false), 600);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [code]);

  const handleGenerate = async () => {
    try {
      await onGenerateCode();
    } catch (err) {
      console.error("Failed to generate code:", err);
    }
  };

  // No code yet - show generate CTA
  if (!code) {
    return (
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sunshine-100 via-fairy-50 to-magic-100 border-[3px] border-sunshine-300 p-6 shadow-[0_8px_32px_rgba(255,217,61,0.25)]">
        {/* Decorative sparkles */}
        <div className="absolute top-4 right-4 w-3 h-3 bg-sunshine-400 rounded-full animate-pulse" />
        <div
          className="absolute top-8 right-12 w-2 h-2 bg-fairy-400 rounded-full animate-pulse"
          style={{ animationDelay: "0.3s" }}
        />
        <div
          className="absolute bottom-8 left-8 w-2 h-2 bg-magic-400 rounded-full animate-pulse"
          style={{ animationDelay: "0.6s" }}
        />

        <div className="relative z-10 text-center space-y-5">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-sunshine-400 to-sunshine-500 rounded-full flex items-center justify-center shadow-lg">
            <GiftIcon className="w-10 h-10 text-neutral-900" />
          </div>

          <div>
            <h3 className="font-display font-bold text-display-sm text-neutral-900 mb-2">
              Share the Magic!
            </h3>
            <p className="text-body-md text-neutral-600 max-w-xs mx-auto">
              Get your referral code and earn a free month when friends subscribe.
            </p>
          </div>

          <Button
            variant="primary"
            size="lg"
            onClick={handleGenerate}
            isLoading={isGenerating}
            className="min-w-[200px]"
          >
            Get My Code
          </Button>
        </div>
      </div>
    );
  }

  // Has code - show it with share options
  return (
    <div className="relative overflow-hidden rounded-3xl bg-cloud border-[3px] border-sunshine-400 shadow-[0_8px_32px_rgba(255,217,61,0.2)]">
      {/* Gradient accent bar */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-sunshine-400 via-fairy-400 to-magic-400" />

      {/* Decorative corner sparkle */}
      <div className="absolute top-6 right-6">
        <SparkleIcon className="w-8 h-8 text-sunshine-500 animate-sparkle" />
      </div>

      <div className="p-6 pt-8">
        <div className="text-center mb-6">
          <p className="text-body-sm font-semibold text-neutral-500 uppercase tracking-wider mb-1">
            Your Referral Code
          </p>
        </div>

        {/* Code display */}
        <div
          className={`
            relative mx-auto max-w-sm
            bg-gradient-to-br from-neutral-50 to-neutral-100
            border-[3px] border-dashed border-neutral-300
            rounded-2xl p-5
            transition-all duration-300
            ${copyAnimating ? "scale-105 border-meadow-500 bg-meadow-50" : ""}
          `}
        >
          <p
            className={`
              text-center font-mono font-bold text-display-md tracking-[0.15em]
              transition-colors duration-300
              ${copied ? "text-meadow-600" : "text-neutral-900"}
            `}
          >
            {code.code}
          </p>

          {/* Copy success checkmark */}
          {copied && (
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-meadow-500 rounded-full flex items-center justify-center animate-[bounceIn_0.4s_ease-out]">
              <CheckIcon className="w-5 h-5 text-white" />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mt-6 justify-center">
          <button
            onClick={handleCopy}
            className={`
              flex items-center gap-2 px-5 py-3
              font-display font-bold text-body-md
              rounded-full border-[3px]
              transition-all duration-200
              active:scale-95
              ${
                copied
                  ? "bg-meadow-100 border-meadow-400 text-meadow-700"
                  : "bg-cloud border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50"
              }
            `}
          >
            {copied ? (
              <>
                <CheckIcon className="w-5 h-5" />
                Copied!
              </>
            ) : (
              <>
                <CopyIcon className="w-5 h-5" />
                Copy Code
              </>
            )}
          </button>

          <button
            onClick={onShare}
            className="
              flex items-center gap-2 px-5 py-3
              font-display font-bold text-body-md
              bg-gradient-to-b from-sunshine-400 to-sunshine-500
              text-neutral-900
              rounded-full border-[3px] border-sunshine-500
              shadow-[0_4px_0_0_theme(colors.sunshine.600)]
              transition-all duration-200
              hover:shadow-[0_6px_0_0_theme(colors.sunshine.600)]
              hover:-translate-y-0.5
              active:shadow-[0_2px_0_0_theme(colors.sunshine.600)]
              active:translate-y-0.5
            "
          >
            <ShareIcon className="w-5 h-5" />
            Share
          </button>
        </div>

        {/* Help text */}
        <p className="text-center text-body-sm text-neutral-500 mt-5">
          Share this code with friends. When they subscribe, you&apos;ll both get a free
          month!
        </p>
      </div>
    </div>
  );
}

// Icons
function GiftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
      />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

export default ReferralCodeCard;
